import { Box, Tooltip } from "@mui/material";
import { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import { useQueryClient } from "@tanstack/react-query";
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import Flex from "../../../../../../../components/Flex";
import { TertiaryNavbar } from "../../../../../../../components/app/provider/patient/chart";
import { useRoleController } from "../../../../../../../components/useRoleController";
import { useReduceDisclosure } from "../../../../../../../hooks/use-reduce-disclosure";
import Authorization from "../../patient-chart/demographics/authorization/Authorization";
import UpsertAuthorizationModal from "../../patient-chart/demographics/authorization/UpsertAuthorizationModal";
import DemographicsNew from "./DemographicsNew";
import UpsertPatientFormDrawer from "./UpsertPatientFormDrawer";
import { TABS_DATA } from "./demographics-contant";
import CheckEligibilityModal from "./eligibility/CheckEligibilityModal";
import EligibilityReportModal from "./eligibility/EligibilityReportModal";
import PatientEligibility from "./eligibility/PatientEligibility";
import PatientInsuranceNew from "./insurance/PatientInsuranceNew";
import UpsertInsuranceFormModal from "./insurance/UpsertInsuranceFormDrawer";
import {
  AddIcon,
  CreatePatientKeyIcon,
  EditPencilIcon,
} from "../../../../../../../components/svg-icons/material-icons";
import theme from "../../../../../../../theme";

/** Map tab value → action button config */
const TAB_ACTIONS: Record<
  string,
  { label: string; variant: "add" | "edit" | "key" | "normal" }
> = {
  "": { label: "Edit Demographics", variant: "edit" },
  insurance: { label: "Add Insurance", variant: "add" },
  eligibility: { label: "Check Eligibility", variant: "normal" },
  authorization: { label: "Add Prior Authorization", variant: "add" },
  "portal-access": { label: "Create Patient Credential", variant: "key" },
};

/** Map tab value → component for minimized (visit-note) view */
const TAB_COMPONENTS: Record<string, ReactNode> = {
  "": <DemographicsNew />,
  insurance: <PatientInsuranceNew />,
  eligibility: <PatientEligibility />,
  authorization: <Authorization />,
};

// As per BA requirement, the Prior Auth tab is hidden on prod until the next release.
const isProduction = import.meta.env.VITE_ENV_NAME === "prod";

const DemoGraphicsLayoutNew = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const isVisitNotePage = window.location.pathname.includes("/visit-note");

  const [localTab, setLocalTab] = useState("");

  const activeTab = useMemo(() => {
    if (isVisitNotePage) return localTab;
    const segments = location.pathname.split("/");
    const last = segments.pop();
    if (last === "demographics") return "";
    return last ?? "";
  }, [location.pathname, isVisitNotePage, localTab]);

  const [searchValue, setSearchValue] = useState("");

  const [
    addPatientInsuranceDisclosure,
    demographicsDisc,
    checkEligibilityDisclosure,
    addAuthorizationDisclosure,
    eligibilityReportDisclosure,
  ] = useReduceDisclosure(5);

  const outletContext: any = useOutletContext() ?? {};
  const patientData = outletContext?.patientData;
  const { hasUpdate: hasUpdatePatientCRUD } = useRoleController("Patient CRUD");
  // ECRH-4589: hide the Check Eligibility action when the user lacks permission.
  const { hasView: hasViewEligibility } =
    useRoleController("Check Eligibility");

  // Refs for child tab actions and extra navbar content
  const childActionRef = useRef<(() => void) | null>(null);
  const [childEndContent, setChildEndContent] = useState<ReactNode>(null);
  const registerChildAction = useCallback((fn: (() => void) | null) => {
    childActionRef.current = fn;
  }, []);

  const handleTabChange = (value: string) => {
    if (isVisitNotePage) {
      setLocalTab(value);
    } else {
      navigate(value || ".");
    }
  };

  const handleAction = () => {
    switch (activeTab) {
      case "":
        if (hasUpdatePatientCRUD) demographicsDisc.open();
        break;
      case "insurance":
        addPatientInsuranceDisclosure.open();
        break;
      case "eligibility":
        if (patientData?.patientInsurances?.length)
          checkEligibilityDisclosure.open();
        break;
      case "authorization":
        addAuthorizationDisclosure.open();
        break;
      case "portal-access":
        childActionRef.current?.();
        break;
    }
  };

  const actionConfig = TAB_ACTIONS[activeTab];
  const showAction =
    actionConfig &&
    (activeTab !== "" || hasUpdatePatientCRUD) &&
    (activeTab !== "eligibility" || hasViewEligibility);

  const hasPatientInsurance = !!patientData?.patientInsurances?.length;
  const eligibilityNoInsurance =
    activeTab === "eligibility" && !hasPatientInsurance;

  // ECRH-4589: remove the Eligibility tab entirely when the user has neither
  // read nor update permission for Check Eligibility.
  const visibleTabs = useMemo(() => {
    let tabs = TABS_DATA;
    if (isVisitNotePage) {
      tabs = tabs.filter((t) => t.value !== "portal-access");
    }
    if (!hasViewEligibility) {
      tabs = tabs.filter((t) => t.value !== "eligibility");
    }
    if (isProduction) {
      tabs = tabs.filter((t) => t.value !== "authorization");
    }
    return tabs;
  }, [isVisitNotePage, hasViewEligibility]);

  return (
    <Flex flexDirection="column" flex={1} width="100%">
      {addAuthorizationDisclosure.opened && (
        <UpsertAuthorizationModal
          disClosureHost={addAuthorizationDisclosure}
          title="Add Prior Authorization"
        />
      )}
      {demographicsDisc.opened && (
        <UpsertPatientFormDrawer
          disclosureHost={demographicsDisc}
          patientData={patientData}
        />
      )}
      {addPatientInsuranceDisclosure.opened && (
        <UpsertInsuranceFormModal
          disClosureHost={addPatientInsuranceDisclosure}
          title="Add Insurance"
        />
      )}
      {checkEligibilityDisclosure.opened && (
        <CheckEligibilityModal
          disclosureHost={checkEligibilityDisclosure}
          eligibilityReportDisclosure={eligibilityReportDisclosure}
          onEligibilityChecked={() => {
            queryClient.invalidateQueries({
              queryKey: ["get-all-eligibility-by-patient-id"],
            });
          }}
        />
      )}
      <EligibilityReportModal disclosureHost={eligibilityReportDisclosure} />

      <TertiaryNavbar
        tabs={visibleTabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder="Search"
        showSearch={false}
        actionLabel={
          !isVisitNotePage && showAction && !eligibilityNoInsurance
            ? actionConfig.label
            : undefined
        }
        actionIcon={
          !isVisitNotePage && showAction && actionConfig.variant === "add" ? (
            <AddIcon />
          ) : actionConfig.variant === "key" ? (
            <CreatePatientKeyIcon />
          ) : actionConfig.variant === "edit" ? (
            <EditPencilIcon fillColor="#ffffff" />
          ) : undefined
        }
        onAction={
          !isVisitNotePage && showAction && !eligibilityNoInsurance
            ? handleAction
            : undefined
        }
        endContent={
          <>
            {!isVisitNotePage &&
              eligibilityNoInsurance &&
              hasViewEligibility && (
                <Tooltip title="No insurance is present for this patient" arrow>
                  <span>
                    <StyledButtonV2 disabled label="Check Eligibility" />
                  </span>
                </Tooltip>
              )}
            {childEndContent}
          </>
        }
      />

      <Box
        flex={1}
        overflow="auto"
        width="100%"
        minHeight={0}
        bgcolor={theme.palette.background.default}
      >
        {isVisitNotePage ? (
          (TAB_COMPONENTS[activeTab] ?? TAB_COMPONENTS[""])
        ) : (
          <Outlet
            context={{ patientData, registerChildAction, setChildEndContent }}
          />
        )}
      </Box>
    </Flex>
  );
};

export default DemoGraphicsLayoutNew;
